import { supabase } from "../supabase";

const CANCELLED_PROVIDER_STATUSES = new Set([
  "anulado",
  "anulada",
  "annulled",
  "cancelled",
  "cancelado",
]);

const normalizeProviderStatus = (value) =>
  String(value || "").trim().toLowerCase();

const isProviderAnnulmentMovement = (movement) => {
  const normalizedType = normalizeProviderStatus(
    movement?.tipo || movement?.type,
  );

  return (
    normalizedType.includes("anul") ||
    normalizedType.includes("reversion") ||
    normalizedType.includes("reversi")
  );
};

const buildProviderTimelineKey = (entry) =>
  `${entry?.entryKind || "mov"}:${entry?.id || ""}`;

export const normalizeProviderBalanceRow = (provider) => {
  if (!provider) return null;

  return {
    ...provider,
    saldo: Number(provider?.saldo || 0),
  };
};

// Ver nota en clientBalances.js: la definicion vive en balanceValues.js.
export { getProviderBalanceValue } from "./balanceValues";
import { getProviderBalanceValue } from "./balanceValues";

export const isProviderPurchaseTimelineRelevant = (purchase) => {
  if (!purchase?.id) return false;
  if (Math.abs(Number(purchase?.total_general || 0)) < 0.000001) return false;

  return !CANCELLED_PROVIDER_STATUSES.has(
    normalizeProviderStatus(purchase?.estado),
  );
};

export const buildProviderAccountTimeline = ({
  provider,
  movements = [],
  purchases = [],
}) => {
  const activePurchases = (purchases || []).filter(
    isProviderPurchaseTimelineRelevant,
  );
  const allPurchaseIds = new Set(
    (purchases || []).map((purchase) => purchase?.id).filter(Boolean),
  );

  const accountRows = (movements || [])
    .filter((movement) => {
      if (!movement?.compra_id) return true;
      if (!allPurchaseIds.has(movement.compra_id)) return true;
      return isProviderAnnulmentMovement(movement);
    })
    .map((movement) => ({
      ...movement,
      entryKind: "account_movement",
      created_at: movement.created_at || movement.fecha || null,
      amount: Number(movement?.monto || 0),
      type: movement?.tipo || "Movimiento",
      timelineKey: buildProviderTimelineKey({
        entryKind: "account_movement",
        id: movement.id,
      }),
    }));

  const purchaseRows = activePurchases.map((purchase) => {
    const isPayment =
      normalizeProviderStatus(purchase?.tipo_comprobante) === "pago";
    const amountAbs = Math.abs(Number(purchase?.total_general || 0));

    return {
      ...purchase,
      compra_id: purchase.id,
      entryKind: "purchase",
      created_at: purchase.fecha || purchase.created_at || null,
      amount: isPayment ? -amountAbs : amountAbs,
      type: isPayment ? "Pago" : purchase?.tipo_comprobante || "Compra",
      timelineKey: buildProviderTimelineKey({
        entryKind: "purchase",
        id: purchase.id,
      }),
    };
  });

  const ascendingRows = [...accountRows, ...purchaseRows].sort((left, right) => {
    const leftTime = new Date(left.created_at || left.fecha).getTime();
    const rightTime = new Date(right.created_at || right.fecha).getTime();
    if (leftTime !== rightTime) return leftTime - rightTime;
    return String(left.timelineKey || "").localeCompare(
      String(right.timelineKey || ""),
    );
  });

  const totalImpact = ascendingRows.reduce(
    (acc, row) => acc + Number(row?.amount || 0),
    0,
  );
  let runningBalance = getProviderBalanceValue(provider) - totalImpact;

  const rowsWithBalance = ascendingRows.map((row) => {
    runningBalance += Number(row?.amount || 0);
    return {
      ...row,
      saldo_resultante: runningBalance,
      current_balance: runningBalance,
    };
  });

  return rowsWithBalance.sort(
    (left, right) =>
      new Date(right.created_at || right.fecha).getTime() -
      new Date(left.created_at || left.fecha).getTime(),
  );
};

export async function fetchProviderAccountSnapshot({
  providerId,
  organizationId,
  supabaseClient = supabase,
}) {
  if (!providerId) {
    return { data: null, error: null, timeline: [] };
  }

  let providerQuery = supabaseClient
    .from("proveedores")
    .select("*")
    .eq("id", providerId);

  let movementsQuery = supabaseClient
    .from("proveedor_movimientos")
    .select("*")
    .eq("proveedor_id", providerId)
    .order("fecha", { ascending: false });

  let purchasesQuery = supabaseClient
    .from("compras")
    .select(
      "id, fecha, tipo_comprobante, numero_comprobante, subtotal, iva, total_general, estado, condicion_pago, proveedor_id, proveedor_nombre, items, sitio_id, created_at, observaciones",
    )
    .eq("proveedor_id", providerId)
    .order("fecha", { ascending: false });

  if (organizationId) {
    providerQuery = providerQuery.eq("organization_id", organizationId);
    movementsQuery = movementsQuery.eq("organization_id", organizationId);
    purchasesQuery = purchasesQuery.eq("organization_id", organizationId);
  }

  const [providerResponse, movementsResponse, purchasesResponse] =
    await Promise.all([
      providerQuery.maybeSingle(),
      movementsQuery,
      purchasesQuery,
    ]);

  if (providerResponse.error) {
    return { data: null, error: providerResponse.error, timeline: [] };
  }

  if (movementsResponse.error) {
    return { data: null, error: movementsResponse.error, timeline: [] };
  }

  if (purchasesResponse.error) {
    return { data: null, error: purchasesResponse.error, timeline: [] };
  }

  const provider = normalizeProviderBalanceRow(providerResponse.data);
  const timeline = buildProviderAccountTimeline({
    provider,
    movements: movementsResponse.data || [],
    purchases: purchasesResponse.data || [],
  });

  return {
    data: provider,
    error: null,
    timeline,
  };
}

export async function fetchActiveOrganizationProvidersWithBalances({
  organizationId,
  supabaseClient = supabase,
}) {
  if (!organizationId) {
    return { data: [], error: null };
  }

  const response = await supabaseClient
    .from("proveedores")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("is_active", true)
    .order("razon_social");

  if (response.error) {
    return { data: [], error: response.error };
  }

  return {
    data: (response.data || []).map(normalizeProviderBalanceRow),
    error: null,
  };
}
